// @givmo/fundraisers-module — fundraiser RECIPIENT picker (EP-WEB3).
//
// A fundraiser's donations split equally across its recipient ITEMS. This picker searches the three
// valid item types and adds/removes them against the fundraiser via the existing item sub-resource
// routes (the company create route only makes the DRAFT; items are managed on `/fundraisers/{id}/…`):
//   - Charities   → `charitiesGetCharities` search   → add/remove `…/charities/{org_ref}`
//   - Funds (ETF) → `etfsGetEtfs` search             → add/remove `…/etfs/{etf_id}`
//   - Collections → `charityCollectionsDiscoverCharityCollections` search → add/remove
//                   `…/charity-collections/{collection_id}` (CC-BE7). Collections ARE valid fundraiser
//                   items (accepted by the existing consumer operation) — unlike direct-donation targets, which reject
//                   them; that is why the /give flow omits collections but this picker includes them.
//
// Every add/remove is a mutation; on success the parent refetches the fundraiser (`onChanged`) so the
// current-items chips + the can-publish gate stay truthful. A backend error (item-list FROZEN after the
// first donation, leaf-charity cap, etc.) is surfaced as a toast — nothing is silently dropped — and a
// 409 (the fundraiser moved under the page: taken down, closed, frozen by a donation) also re-reads it.
// A locked list (`disabled`) draws its chips without the remove "×" (FCM-WEB18, the ruled mockup): a
// greyed "×" made a read-only list look removable. The employer portal and the company console's module
// both render this picker, so both get it.

import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  charitiesGetCharitiesOptions,
  etfsGetEtfsOptions,
  charityCollectionsDiscoverCharityCollectionsOptions,
  fundraisersAddCharityToFundraiserMutation,
  fundraisersAddEtfToFundraiserMutation,
  fundraisersAddCollectionToFundraiserMutation,
  fundraisersRemoveCharityFromFundraiserMutation,
  fundraisersRemoveEtfFromFundraiserMutation,
  fundraisersRemoveCollectionFromFundraiserMutation,
} from '@givmo/api-client/query';
import { isGivmoError, type FundraiserOut } from '@givmo/api-client';
import {
  Avatar,
  Button,
  EmptyState,
  Icon,
  SearchInput,
  Skeleton,
  Tabs,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { Check, HeartOff, Plus, X } from 'lucide-react';
import { useLivePage } from './use-live-page';
import {
  currentRecipients,
  extractCharityResults,
  extractCollectionResults,
  extractEtfResults,
  type RecipientKind,
  type RecipientSearchResult,
} from './recipients-decode';

const PREFIX = 'recipient-picker';

const TABS: readonly { kind: RecipientKind; label: string }[] = [
  { kind: 'charity', label: 'Charities' },
  { kind: 'etf', label: 'Funds' },
  { kind: 'collection', label: 'Collections' },
];

export interface RecipientPickerProps {
  fundraiser: FundraiserOut;
  /** Refetch the fundraiser after any add/remove so chips + the can-publish gate reflect the truth. */
  onChanged: () => void | Promise<unknown>;
  /** Soft-disable adding/removing (item list frozen after the first donation). The backend is the
   *  authoritative guard; this just quiets the controls and explains why. */
  disabled?: boolean;
  /** The host can explain another authoritative read-only state without claiming a donation occurred. */
  disabledReason?: string;
}

export function RecipientPicker({
  fundraiser,
  onChanged,
  disabled,
  disabledReason,
}: RecipientPickerProps) {
  const live = useLivePage();
  const items = useMemo(() => currentRecipients(fundraiser), [fundraiser]);
  const addedIds = useMemo(() => new Set(items.map((i) => `${i.kind}:${i.id}`)), [items]);

  const fundraiserId = fundraiser.id;

  const onMutationError = (error: unknown) => {
    if (live())
      toast.error(isGivmoError(error) ? error.message : 'Something went wrong. Please try again.');
    // A 409 means the fundraiser moved under the page, so re-read it: the chips and the lock then
    // show what the server holds (FCM-WEB18).
    if (isGivmoError(error) && error.status === 409) void onChanged();
  };
  const onMutationDone = async () => {
    await onChanged();
  };

  // One add/remove mutation per kind (each is a distinct SDK op with its own path shape).
  const addCharity = useMutation({
    ...fundraisersAddCharityToFundraiserMutation(),
    onSuccess: onMutationDone,
    onError: onMutationError,
  });
  const addEtf = useMutation({
    ...fundraisersAddEtfToFundraiserMutation(),
    onSuccess: onMutationDone,
    onError: onMutationError,
  });
  const addCollection = useMutation({
    ...fundraisersAddCollectionToFundraiserMutation(),
    onSuccess: onMutationDone,
    onError: onMutationError,
  });
  const removeCharity = useMutation({
    ...fundraisersRemoveCharityFromFundraiserMutation(),
    onSuccess: onMutationDone,
    onError: onMutationError,
  });
  const removeEtf = useMutation({
    ...fundraisersRemoveEtfFromFundraiserMutation(),
    onSuccess: onMutationDone,
    onError: onMutationError,
  });
  const removeCollection = useMutation({
    ...fundraisersRemoveCollectionFromFundraiserMutation(),
    onSuccess: onMutationDone,
    onError: onMutationError,
  });

  const anyPending =
    addCharity.isPending ||
    addEtf.isPending ||
    addCollection.isPending ||
    removeCharity.isPending ||
    removeEtf.isPending ||
    removeCollection.isPending;

  function add(result: RecipientSearchResult) {
    if (disabled || anyPending) return;
    if (result.kind === 'charity')
      addCharity.mutate({ path: { fundraiser_id: fundraiserId, charity_id: result.id } });
    else if (result.kind === 'etf')
      addEtf.mutate({ path: { fundraiser_id: fundraiserId, etf_id: result.id } });
    else addCollection.mutate({ path: { fundraiser_id: fundraiserId, collection_id: result.id } });
  }

  function remove(itemKind: RecipientKind, id: string) {
    if (disabled || anyPending) return;
    if (itemKind === 'charity')
      removeCharity.mutate({ path: { fundraiser_id: fundraiserId, charity_id: id } });
    else if (itemKind === 'etf')
      removeEtf.mutate({ path: { fundraiser_id: fundraiserId, etf_id: id } });
    else removeCollection.mutate({ path: { fundraiser_id: fundraiserId, collection_id: id } });
  }

  return (
    <div className="flex flex-col gap-16" {...testIdProp(`${PREFIX}`)}>
      {/* ── Current recipients (removable chips) ── */}
      <div className="flex flex-col gap-8">
        <span className="text-s13 font-w600 text-text-strong">Recipients ({items.length})</span>
        {items.length === 0 ? (
          <p className="text-s13 text-text-tertiary" {...testIdProp(`${PREFIX}-empty`)}>
            Add at least one charity, fund, or collection. Donations split equally across them.
          </p>
        ) : (
          <ul className="flex flex-wrap gap-8" {...testIdProp(`${PREFIX}-items`)}>
            {items.map((item) => (
              <li
                key={`${item.kind}:${item.id}`}
                className={`flex max-w-full items-center gap-8 rounded-full border border-border-subtle bg-surface py-4 pl-4 ${disabled ? 'pr-12' : 'pr-8'}`}
                {...testIdProp(`${PREFIX}-item-${item.id}`)}
              >
                <Avatar src={item.imageUrl ?? undefined} name={item.name} size="xs" />
                {/* A name wraps, never cut off (tester-5's item 12). */}
                <span className="min-w-0 break-words text-s13 font-w500 text-text-strong">
                  {item.name}
                </span>
                {!disabled && (
                  <button
                    type="button"
                    aria-label={`Remove ${item.name}`}
                    disabled={anyPending}
                    onClick={() => remove(item.kind, item.id)}
                    className="rounded-full p-2 text-text-tertiary outline-none transition-colors hover:text-danger-600 focus-visible:text-danger-600 disabled:opacity-40"
                    {...testIdProp(`${PREFIX}-remove-${item.id}`)}
                  >
                    <Icon icon={X} size={14} aria-hidden />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        {disabled ? (
          <p className="text-s12 text-text-tertiary" {...testIdProp(`${PREFIX}-frozen`)}>
            {disabledReason ?? 'Recipients are locked once the fundraiser has a donation.'}
          </p>
        ) : null}
      </div>

      {/* ── Search + add ── */}
      {!disabled && <RecipientSearch addedIds={addedIds} adding={anyPending} onAdd={add} />}
    </div>
  );
}

/**
 * The picker's search half: the three recipient kinds' tabs, the name search and its results, each with
 * Add. The picker adds a result to its fundraiser; a campaign's optional list (FCM-WEB25) collects it.
 * Only the active tab's search runs (enabled gate) — never three concurrent searches.
 */
export function RecipientSearch({
  addedIds,
  adding,
  onAdd,
  testIdPrefix = PREFIX,
  compact = false,
}: {
  addedIds: Set<string>;
  adding: boolean;
  onAdd: (result: RecipientSearchResult) => void;
  testIdPrefix?: string;
  /** In a narrow home (a dialog): the tabs at their phone size, and the results in a scrolling box. */
  compact?: boolean;
}) {
  const [kind, setKind] = useState<RecipientKind>('charity');
  const [search, setSearch] = useState('');

  const query = { q: search.trim().length > 0 ? search.trim() : null, 'page[limit]': 20 } as const;
  const charities = useQuery({
    ...charitiesGetCharitiesOptions({ query }),
    enabled: kind === 'charity',
  });
  const etfs = useQuery({ ...etfsGetEtfsOptions({ query }), enabled: kind === 'etf' });
  const collections = useQuery({
    ...charityCollectionsDiscoverCharityCollectionsOptions({ query }),
    enabled: kind === 'collection',
  });

  const active = kind === 'charity' ? charities : kind === 'etf' ? etfs : collections;
  const results: RecipientSearchResult[] =
    kind === 'charity'
      ? extractCharityResults(charities.data)
      : kind === 'etf'
        ? extractEtfResults(etfs.data)
        : extractCollectionResults(collections.data);

  return (
    <div className="flex flex-col gap-12">
      <Tabs.Root value={kind} onValueChange={(v) => setKind(v as RecipientKind)}>
        <Tabs.List>
          {TABS.map((t) => (
            <Tabs.Tab
              key={t.kind}
              value={t.kind}
              className={compact ? 'px-10 text-s14' : 'px-10 text-s14 sm:px-[35px] sm:text-s20'}
              testId={`${testIdPrefix}-tab-${t.kind}`}
            >
              {t.label}
            </Tabs.Tab>
          ))}
        </Tabs.List>
      </Tabs.Root>

      <SearchInput
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onClear={() => setSearch('')}
        placeholder="Search by name…"
        aria-label="Search recipients"
        testId={`${testIdPrefix}-search`}
      />

      <div className={compact ? 'max-h-[280px] overflow-y-auto' : undefined}>
        <PickerResults
          loading={active.isPending && active.fetchStatus !== 'idle'}
          error={active.isError}
          results={results}
          addedIds={addedIds}
          adding={adding}
          onAdd={onAdd}
          onRetry={() => void active.refetch()}
          testIdPrefix={testIdPrefix}
        />
      </div>
    </div>
  );
}

export function PickerResults({
  loading,
  error,
  results,
  addedIds,
  adding,
  onAdd,
  onRetry,
  testIdPrefix = PREFIX,
}: {
  loading: boolean;
  error: boolean;
  results: RecipientSearchResult[];
  addedIds: Set<string>;
  adding: boolean;
  onAdd: (result: RecipientSearchResult) => void;
  onRetry: () => void;
  testIdPrefix?: string;
}) {
  if (loading) {
    return (
      <ul className="flex flex-col gap-8" {...testIdProp(`${testIdPrefix}-results`)}>
        {Array.from({ length: 4 }).map((_, i) => (
          <li
            key={i}
            className="flex items-center gap-10 rounded-lg border border-border-subtle p-12"
          >
            <Skeleton className="size-32 shrink-0 rounded-full" />
            <Skeleton className="h-12 flex-1" />
          </li>
        ))}
      </ul>
    );
  }
  if (error) {
    return (
      <div className="flex flex-col items-center gap-8 py-16 text-center">
        <p className="text-s14 font-w500 text-danger-700">Couldn’t load results.</p>
        <button
          type="button"
          onClick={onRetry}
          className="text-s13 font-w600 text-brand-600 outline-none hover:underline focus-visible:underline"
        >
          Try again
        </button>
      </div>
    );
  }
  if (results.length === 0) {
    return (
      <EmptyState
        icon={<Icon icon={HeartOff} size={20} aria-hidden />}
        title="No matches"
        description="Try a different search."
      />
    );
  }
  return (
    <ul className="flex flex-col gap-8" {...testIdProp(`${testIdPrefix}-results`)}>
      {results.map((r) => {
        const added = addedIds.has(`${r.kind}:${r.id}`);
        return (
          <li
            key={`${r.kind}:${r.id}`}
            className="flex items-center gap-10 rounded-lg border border-border-subtle p-12"
            {...testIdProp(`${testIdPrefix}-result-${r.id}`)}
          >
            <Avatar src={r.imageUrl ?? undefined} name={r.name} size="sm" />
            <div className="flex min-w-0 flex-col">
              {/* A name wraps, never cut off, and every row's Add keeps its width (tester-5's item 12). */}
              <span className="break-words font-w600 text-text-strong">{r.name}</span>
              {r.subtitle !== null && (
                <span className="break-words text-s12 text-text-tertiary">{r.subtitle}</span>
              )}
            </div>
            <Button
              appearance="saas"
              tone={added ? 'ghost' : 'secondary'}
              size="sm"
              className="ml-auto shrink-0"
              disabled={added || adding}
              leadingIcon={<Icon icon={added ? Check : Plus} size={14} aria-hidden />}
              onClick={() => onAdd(r)}
              testId={`${testIdPrefix}-add-${r.id}`}
            >
              {added ? 'Added' : 'Add'}
            </Button>
          </li>
        );
      })}
    </ul>
  );
}
